/**
 * @file   message.js
 *         Implementation of DcpMessage class.  DcpMessages include requests, responses, and batches --
 *         but not Acks.  See grammar in spec for more information.
 * @author Wes Garland, wes@distributive.network
 * @date   Nov 2023
 */
'use strict';

const { assert }    = require('dcp/common/dcp-assert');
const { Reference } = require('dcp/common/dcp-timers');
const { DCPError }  = require('dcp/common/dcp-error');
const { randStr21 } = require('./utils');

exports.DcpMessage = DcpMessage;
const dcpMessageAuth  = require('./dcp-message-auth');
const requestValidity = require('./request-validity');
const debugging = require('dcp/debugging').scope('dcp');

/**
 * DcpMessage base class. This class is exported for the sole purpose of instanceof comparison and
 * should not be instanciated. DcpMessage inherits from Reference, adding ref and unref methods; default
 * is unref'd.
 */
function DcpMessage()
{
  Reference.call(this, debugging, () => this.connection.debugLabel);
  DcpMessage.seq = (DcpMessage.seq || 0) + 1;
  this.id = this.connection.seq + '-' + DcpMessage.seq + '-' + randStr21();

  if (this.constructor === DcpMessage)
    throw new TypeError('cannot instanciate DcpMessage base class');
}
DcpMessage.prototype = Object.create(Reference.prototype);
DcpMessage.prototype.constructor = DcpMessage;

/* Add fancy output for console.log in Node.js */
if (require('dcp/common/dcp-env').platform === 'nodejs')
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const inspect = requireNative('util').inspect.custom;
  DcpMessage.prototype[inspect] = function DcpMessage$$inspect()
  {
    let details = this.payload instanceof this.connection.ErrorPayload
        ? `${this.payload.name}: ${this.payload.message}`
        : this.payload?.operation || '';
    if (details)
      details = ':' + details;
    return `[Object ${String(this.connection.role)[0]}#${this.connection.seq}(${this.constructor.name + details}) ${this.id.slice(0,12)}]`;
  }
}

/**
 * Enqueue a message for transmission on the current connection.
 */
DcpMessage.prototype.send = function DcpMessage$$send()
{
  return this.connection.send(this);
}

/**
 * The "network" form of a message is what actually goes out to the peer over the wire. In the case
 * where the transmitted message will not be in a batch, ackToken and nonce fields are added by the
 * signing code.
 */
DcpMessage.prototype.toNetwork = function DcpMessage$$toNetwork()
{
  return {
    id:      this.id,
    type:    this.type,
    success: this.success,
    payload: this.payload,
    auth:    this.auth || {},
    dcpsid:  this.connection.dcpsid,
    time:    this.connection.currentTime(),
  };
}

/**
 * DcpMessages are signed with the Ethereum message signing code as they are sealed (turned into
 * strings) for transmission over the network. We add a nonce property which was recently
 * received from the peer, which helps mitigate against MITM/CSRF/Replay attacks. We also send an
 * ackToken, which is used to track incoming Acks which tell us that this DcpMessage has been
 * received by the peer.
 *
 * @returns {Promise<string>}    the sealed message
 *
 * @note spec lists this as an API function, but consumption of the nonce suggests that that
 *       should be changed
 */
DcpMessage.prototype.sign = async function DcpMessage$$sign(ackToken)
{
  assert(this.connection.lastRxNonce !== false);

  const networkObj = this.toNetwork();
  networkObj.ackToken = ackToken;
  networkObj.nonce = this.connection.lastRxNonce;
  this.connection.lastRxNonce = false; /* eagerly consume nonce - ~mutex */

  /* nonce: null->first message, false->no nonce available, string->nonce available */
  assert(typeof networkObj.nonce === 'string' || networkObj.nonce === null);

  if (this.type === 'request' || this.type === 'notification')
  {
    assert(this.payload?.validity?.time);
    // Resolve any allowed accessors/guardians that are the peerAddress or our identity.address. If we'd authorized
    // a message before connecting, those values would be undefined. They will be defined at the point of signing,
    // since we sign only when the message is about to be sent on an established connection.
    for (const allow of this.payload.allow)
    {
      if (allow.guardian === 'peerAddress')
        allow.guardian = this.connection.peerAddress;
      if (allow.accessor === 'identity.address')
        allow.accessor = this.connection.identity.address;
    }
    networkObj.auth = this.auth || {};
    for (const [resourceAddress, ks] of this.resourceKeystores)
      networkObj.auth[resourceAddress] = await ks.makeSignature(networkObj.payload);
  }

  if (!this.connection.identity)
    await this.connection.identityPromise;
  await this.connection.identity.unlock(null, this.connection.options.identityUnlockTimeout, true);

  return this.connection.identity.makeSignedMessage(networkObj);
}

/**
 * Static method of DcpMessage to instanciate a new object and populate it with details from its
 * network representation.
 *
 * @param {function} Ctor               the constructor to use for instanciation.
 * @param {string}   signedMessageBody  the body property of an Ethereum signed message.
 * @returns instanceof a class derived from DcpMessage
 *
 * @note the factory functions which create the Request and Response classes use this static
 *       method to implement equivalent functions, but those functions do not need the Ctor
 *       parameter.
 */
DcpMessage.from = function DcpMessage$$from(Ctor, signedMessageBody)
{
  const message = new Ctor();
  const body = signedMessageBody;

  message.id      = body.id;
  message.dcpsid  = body.dcpsid;
  message.payload = body.payload;
  message.auth    = body.auth;
  message.success = body.success;
  message.time    = body.time;

  /* Replace payload.data with the deserialized version of payload.jsonData if present */
  if (message.payload?.jsonData)
  {
    try
    {
      message.payload.data = JSON.parse(message.payload.jsonData);
    }
    catch (error)
    {
      debugging() && console.error(`Connection with dcpsid ${message.dcpsid} failed to parse jsonData:`, error);
      throw new DCPError('malformed message payload.jsonData', 'EINVAL');
    }
    delete message.payload.jsonData;
  }

  return message;
}

/**
 * Factory function that creates connection-specific Notification constructors. These constructors inherit
 * from DcpMessage, and reference the connection internally by closure.
 *
 * @param {object} connection    instance of Connection
 * @returns {function(object): void}
 */
exports.notificationCtorFactory = function message$$notificationCtorFactory(connection)
{
  /**
   * Request constructor.
   */
  function Notification(data)
  {
    this.type = 'notification';
    this.connection = connection;
    DcpMessage.call(this);
    this.payload = { data };

    this.resourceKeystores = new Map(); // resourceAddress => keystore
    this.auth = {};
    this.payload.allow = [];
    this.payload.validity = {
      stamp: randStr21(),
      ttl:   connection.options.ttl.default,
      time:  null /* set when placed on deck if still null */
    };
  }

  Notification.from = (smb) => {
    const notification = DcpMessage.from(Notification, smb);
    return notification;
  }

  Notification.prototype = Object.create(DcpMessage.prototype);
  Notification.prototype.constructor = Notification;
  Notification.prototype.authorize     = dcpMessageAuth.authorize;
  Notification.prototype.doesAuthorize = dcpMessageAuth.doesAuthorize;
  Notification.prototype.validityError = requestValidity.validityError;

  return Notification;
}

/**
 * Factory function that creates connection-specific Request constructors. These constructors inherit
 * from DcpMessage, and reference the connection internally by closure.
 *
 * @param {object} connection    instance of Connection
 * @returns {function(object): void}
 */
exports.requestCtorFactory = function message$$requestCtorFactory(connection)
{
  /**
   * Request constructor.
   */
  function Request()
  {
    this.type = 'request';
    this.connection = connection;
    DcpMessage.call(this);

    /* Sort out the ctor overload defined in the spec */
    switch(arguments.length)
    {
      default:
        assert(!'reached');
        break;
      case 0: /* form 1 */
        this.payload = {};
        break;
      case 1:
      {
        const [ payload ] = arguments;
        if (typeof payload === 'object') /* form 2 */
        {
          this.payload = payload;
          break;
        }
      }
      /* fallthrough */
      case 2: /* form 3 */
      {
        const [ operation, data ] = arguments;
        assert(typeof operation === 'string');
        this.payload = { operation, data };
      }
    }

    this.resourceKeystores = new Map(); // resourceAddress => keystore
    this.auth = {};
    this.payload.allow = [];
    this.payload.validity = {
      stamp: randStr21(),
      ttl:   connection.options.ttl.default,
      time:  null /* set when placed on deck if still null */
    };
  }

  Request.from = (smb) => {
    const request = DcpMessage.from(Request, smb);
    return request;
  }

  Request.prototype = Object.create(DcpMessage.prototype);
  Request.prototype.constructor = Request;
  Request.prototype.respond = function Request$$respond(...args) {
    const response = new connection.Response(this, ...args);
    return response.send();
  };
  Request.prototype.authorize     = dcpMessageAuth.authorize;
  Request.prototype.doesAuthorize = dcpMessageAuth.doesAuthorize;
  Request.prototype.validityError = requestValidity.validityError;

  return Request;
}

/**
 * Factory function that creates connection-specifict Response  constructors. These constructors inherit
 * from DcpMessage, and reference the connection internally by closure.
 *
 * @param {object} connection    instance of Connection
 * @returns {Function}
 */
exports.responseCtorFactory = function message$$responseCtorFactory(connection)
{
  /**
   * Response constructor.
   */
  function Response()
  {
    this.type = 'response';
    this.connection = connection;
    DcpMessage.call(this);

    /* Sort out the ctor overload defined in the spec */
    switch(arguments.length)
    {
      default:
        assert(!'reached');
        break;
      case 0: /* form 1 */
        break;
      case 2:
      {
        const request = arguments[0];
        this.id = request.id;

        if (arguments[1] instanceof Error)
        {
          this.payload = new connection.ErrorPayload(arguments[1]);
          this.success = false;
        }
        else if (arguments[1] instanceof connection.ErrorPayload)
        {
          this.payload = arguments[1];
          this.success = false;
        }
        else
        {
          this.payload = arguments[1];
          this.success = true;
        }
        break;
      }
    }
  }
  Response.prototype = Object.create(DcpMessage.prototype);
  Response.prototype.constructor = Response;

  Response.from = responseFrom;
  function responseFrom(body)
  {
    const message = DcpMessage.from(Response, body);
    if (body.success !== true)
    {
      if (body.success !== false)
        throw new DCPError('malformed response', 'EINVAL');

      message.success = false;
      message.payload = connection.ErrorPayload.from(message.payload);
    }
    return message;
  }

  return Response;
}

/**
 * Factory function that creates connection-specific Batch constructors. These constructors inherit
 * from DcpMessage, and reference the connection internally by closure.
 *
 * @param {object} connection    instance of Connection
 * @returns {Function}
 */
exports.batchCtorFactory = function message$$batchCtorFactory(connection)
{
  /**
   * Batch constructor.
   */
  function Batch(batch)
  {
    assert(batch?.length > 0);

    this.type = 'batch';
    this.connection = connection;
    this.payload = batch;
    DcpMessage.call(this);
    this.unref();
  }
  Batch.prototype = Object.create(DcpMessage.prototype);
  Batch.prototype.constructor = Batch;

  return Batch;
}
